import type {
  InteractiveQuestionState,
  InteractiveQuestionsState,
  PlayerUserAnswer,
} from '@genially/p2p-lib';

/**
 * The rules the whole analytics panel shares. Three views read them now — the session
 * list, the session full screen and the analytics view of a single question — so they
 * live here rather than next to any one of them: changing the deduplication rule or the
 * participant universe has to change all three at once or the panel contradicts itself.
 */

export const toPercentage = (value: number, total: number) =>
  total === 0 ? 0 : Math.round((value / total) * 100);

/**
 * The placeholder the view auto-submits for a player who had not answered when the
 * question was resolved (or when their timer ran out): an empty answer flagged
 * `isTimeEnd`, sent so the score counts the miss. Autocorrective types send it with
 * `isCorrect: false`, so reading it as an answer turns silence into a wrong answer —
 * a room where nobody answered reads as 100% participation and 100% error rate.
 */
const isAutoSubmittedOnResolve = ({ answer }: PlayerUserAnswer) =>
  answer.isTimeEnd === true;

/**
 * One answer per player: the latest one they sent for that question. Retries would
 * otherwise count as separate answers and punish whoever tried again. Same rule the
 * ranking already applies, so the two can never disagree.
 *
 * Players are told apart by name, not by `player.id`: the id is the peer identifier the
 * server hands out on every connection, so a mid-session reconnection would otherwise
 * split one person into two participants with half a score each. The cost is that two
 * people picking the same alias merge into one, the less visible of the two errors.
 *
 * Auto-submitted empty answers are not answers: whoever only has one of those counts as
 * having said nothing, which is what the whole panel reads as "not answered".
 */
export const latestAnswerPerPlayer = (
  state: InteractiveQuestionState,
): PlayerUserAnswer[] => {
  const byUsername = new Map<string, PlayerUserAnswer>();

  state.playerUserAnswers.forEach(playerUserAnswer => {
    if (isAutoSubmittedOnResolve(playerUserAnswer)) {
      return;
    }

    const current = byUsername.get(playerUserAnswer.player.username);

    if (!current || playerUserAnswer.answer.timestamp > current.answer.timestamp) {
      byUsername.set(playerUserAnswer.player.username, playerUserAnswer);
    }
  });

  return Array.from(byUsername.values());
};

/**
 * Who counts as a participant of the session: whoever answered at least once, plus
 * whoever is connected right now.
 *
 * Only the first half would make a room of twenty where three people answer the opening
 * question report 100% participation and read the hit rate over three people. Only the
 * second half would drop whoever closed their tab, letting the denominator shrink below
 * the answers already counted. The union can only shrink when someone who never answered
 * anything leaves, which is exactly who should stop counting.
 */
export const buildParticipantUsernames = ({
  interactiveQuestionAnswers,
  connectedUsernames,
}: {
  interactiveQuestionAnswers: InteractiveQuestionsState;
  connectedUsernames: string[];
}): string[] => {
  const usernames = new Set<string>(connectedUsernames);

  Object.values(interactiveQuestionAnswers).forEach(state => {
    state.playerUserAnswers.forEach(({ player }) => {
      usernames.add(player.username);
    });
  });

  return Array.from(usernames).sort((a, b) => a.localeCompare(b));
};
